import { forwardRef, useEffect, useRef, useState } from 'react';
import './MultiSelect.css';
import { clsx } from 'clsx';
import type { SelectOption } from 'src/components/Select/Select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { Checkbox } from 'src/components/ui/checkbox';
import { SelectChevronIcon } from 'src/assets/icons/SelectChevronIcon';

interface MultiSelectProps {
  options?: SelectOption[];
  // Called with the new selection, the clicked option and whether it was checked.
  onChange?: (selected: SelectOption[], option: SelectOption, checked: boolean) => void;
  onClose?: (selected: SelectOption[]) => void;
  values?: SelectOption[];
  align?: string;
  dropdownMenuClassNames?: string;
}

export const MultiSelect = forwardRef<HTMLDivElement, MultiSelectProps>(
  (
    {
      options = [],
      onChange = () => {},
      onClose = () => {},
      values = [],
      align,
      dropdownMenuClassNames = '',
    },
    ref,
  ) => {
    const [showMenu, setShowMenu] = useState(false);
    const [selectedValues, setSelectedValues] = useState([options[0]]);
    const inputRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      setSelectedValues(values);
    }, [values]);

    useEffect(() => {
      if (!showMenu) {
        onClose?.(selectedValues);
      }
    }, [showMenu]);

    useEffect(() => {
      const handler = (e: MouseEvent) => {
        const target = e.target as Node;

        if (
          inputRef.current &&
          !inputRef.current.contains(target) &&
          menuRef.current &&
          !menuRef.current.contains(target)
        ) {
          setShowMenu(false);
        }
      };

      globalThis.addEventListener('click', handler);
      return () => {
        globalThis.removeEventListener('click', handler);
      };
    }, []);

    const handleInputClick = () => {
      setShowMenu((prevState) => !prevState);
    };

    const onItemClick = (checked: boolean, option: SelectOption) => {
      let selected;

      if (checked) {
        selected = [...selectedValues, option];
        setSelectedValues(selected);
      } else {
        selected = selectedValues.filter((value) => value.label !== option.label);
        setSelectedValues(selected);
      }

      onChange(selected, option, checked);
    };

    const isSelected = (option: SelectOption) => {
      if (!selectedValues.length) {
        return false;
      }

      return selectedValues.some((value) => value.label === option.label);
    };

    return (
      <div ref={ref} className="custom--multiselect-container">
        <button
          type="button"
          ref={inputRef}
          onClick={handleInputClick}
          className={clsx(
            showMenu ? 'rounded-t-[5px]' : 'rounded-[5px]',
            'dropdown-input',
            'w-full text-left',
          )}
        >
          <div
            className={`flex items-center gap-[7px] dropdown-selected-value ${!selectedValues || selectedValues.length === 0 ? 'placeholder' : ''}`}
          >
            <p className="w-[150px] text-ellipsis	whitespace-nowrap overflow-hidden">
              {selectedValues.map((selectedValue, index, arr) => {
                return (
                  <span
                    key={`${selectedValue.label.toLowerCase()}_${index}`}
                    className="text-start hyphens-auto"
                  >
                    {selectedValue.label}
                    {index !== arr.length - 1 && ', '}
                  </span>
                );
              })}
            </p>
          </div>
          <div className="dropdown-tools">
            <div className="dropdown-tool">
              <SelectChevronIcon isOpen={showMenu} />
            </div>
          </div>
        </button>
        {showMenu && (
          <div
            className={clsx(
              dropdownMenuClassNames,
              `dropdown-menu relative alignment--${align || 'auto'} w-full max-h-80`,
            )}
            ref={menuRef}
          >
            {options?.map((option, index) => {
              const key = `${option?.label.toLowerCase().replace(/\s/g, '-')}-${index}`;

              return (
                <TooltipProvider key={key}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        className={`flex flex-row items-center gap-[7px] w-full dropdown-item ${isSelected(option) && 'selected'}`}
                      >
                        <Checkbox
                          id={`multiselect-${key}`}
                          checked={isSelected(option)}
                          className={'rounded-[2px]'}
                          onCheckedChange={(checked) => {
                            onItemClick(checked === true, option);
                          }}
                        />
                        <label htmlFor={`multiselect-${key}`}>{option?.label}</label>
                      </div>
                    </TooltipTrigger>
                    {option?.toolTipDescription && (
                      <TooltipContent className="max-w-2xl">
                        {option.toolTipDescription}
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              );
            })}
          </div>
        )}
      </div>
    );
  },
);
